import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2, Search, Plus, ShieldOff, Unlock, CheckCircle, XCircle } from "lucide-react";
import {
  listarUsuarios,
  excluirUsuario,
  desativarUsuario,
  ativarUsuario,
  listarUsuariosDistribuidor,
  atualizarUsuarioDistribuidor,
  excluirUsuarioDistribuidor,
} from "../services/api";
import { portalDistribuidor } from "../utils/portalModo";

const ensureArray = (v) => (Array.isArray(v) ? v : []);

const ListaUsuarios = ({ token }) => {
  const navigate = useNavigate();

  const [usuariosOriginais, setUsuariosOriginais] = useState([]);
  const [busca, setBusca] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = portalDistribuidor()
        ? await listarUsuariosDistribuidor(token)
        : await listarUsuarios(token);
      setUsuariosOriginais(ensureArray(res?.data));
    } catch (e) {
      console.error("Erro ao listar usuários:", e);
      setUsuariosOriginais([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [token]);

  const usuarios = useMemo(() => {
    const termo = (busca || "").toLowerCase();
    const base = ensureArray(usuariosOriginais);
    if (!termo) return base;

    return base.filter(
      (u) =>
        (u.email || "").toLowerCase().includes(termo) ||
        (u.nome || "").toLowerCase().includes(termo)
    );
  }, [usuariosOriginais, busca]);

  const handleEditar = (id) => navigate(`/usuarios/editar/${id}`);
  const handleNovo = () => navigate(`/usuarios/novo`);

  const handleExcluir = async (id) => {
    if (!window.confirm("Tem certeza que deseja excluir este usuário?")) return;
    try {
      if (portalDistribuidor()) await excluirUsuarioDistribuidor(id, token);
      else await excluirUsuario(id, token);
      await fetchData();
      alert("Usuário excluído com sucesso!");
    } catch (e) {
      console.error(e);
      alert("Erro ao excluir usuário.");
    }
  };

  const handleToggleAtivo = async (u) => {
    try {
      if (portalDistribuidor()) {
        const proximo = !u.ativo;
        if (!window.confirm(proximo ? `Ativar o usuário "${u.nome}"?` : `Desativar o usuário "${u.nome}"?`)) return;
        await atualizarUsuarioDistribuidor(u.id, {
          nome: u.nome,
          email: u.email,
          telefone: u.telefone || "",
          ativo: proximo,
        }, token);
      } else if (u.ativo) {
        if (!window.confirm(`Desativar o usuário "${u.nome}"?`)) return;
        await desativarUsuario(u.id, token);
      } else {
        if (!window.confirm(`Ativar o usuário "${u.nome}"?`)) return;
        await ativarUsuario(u.id, token);
      }
      await fetchData();
    } catch (e) {
      console.error(e);
      alert("Erro ao alterar status do usuário.");
    }
  };

  if (loading) {
    return (
      <div className="p-6 text-center text-gray-600 dark:text-slate-200">
        Carregando usuários...
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="zen-card rounded-2xl p-6 shadow-sm dark:backdrop-blur">
        {/* topo */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Usuários
            </h2>
            <p className="text-xs text-gray-500 dark:text-slate-300/80 mt-1">
              Gerencie usuários de acesso do seu painel.
            </p>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-400"
                size={20}
              />
              <input
                type="text"
                placeholder="Buscar usuário..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                className="
                  pl-10 pr-4 py-2 w-64 rounded-xl border outline-none transition
                  zen-card border-gray-300 text-gray-900 placeholder:text-gray-400
                  focus:ring-2 focus:ring-blue-500/60
                  dark:bg-slate-950/40 dark:border-white/10 dark:text-white dark:placeholder:text-slate-400
                  dark:focus:ring-blue-400/40
                "
              />
            </div>

            <button
              className="
                inline-flex items-center px-4 py-2 rounded-xl font-semibold text-sm transition
                bg-blue-600 text-white hover:bg-blue-500
              "
              onClick={handleNovo}
            >
              <Plus size={20} className="mr-2" />
              Novo Usuário
            </button>
          </div>
        </div>

        {/* tabela */}
        <div className="overflow-x-auto">
          <div className="zen-card rounded-2xl border border-gray-200 dark:bg-white/5 dark:border-white/10">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-white/5">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                    Email
                  </th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                    Nome
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                    Ativo
                  </th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-600 dark:text-slate-200">
                    Ações
                  </th>
                </tr>
              </thead>

              <tbody className="text-gray-700 dark:text-slate-200">
                {usuarios.map((u) => (
                  <tr
                    key={u.id}
                    className="border-t border-gray-200 hover:bg-gray-50 transition dark:border-white/10 dark:hover:bg-white/5"
                  >
                    <td className="py-3 px-4">{u.email || "-"}</td>
                    <td className="py-3 px-4">{u.nome || "-"}</td>

                    <td className="text-center py-3 px-4">
                      {u.ativo ? (
                        <CheckCircle className="text-green-600 dark:text-emerald-300 inline" size={20} />
                      ) : (
                        <XCircle className="text-red-600 dark:text-red-300 inline" size={20} />
                      )}
                    </td>

                    <td className="text-center py-3 px-4">
                      <div className="inline-flex items-center gap-2">
                        <button
                          className="p-2 text-blue-600 hover:bg-blue-100 rounded-full transition-colors dark:text-blue-300 dark:hover:bg-blue-500/10"
                          onClick={() => handleEditar(u.id)}
                          title="Editar Usuário"
                        >
                          <Edit size={18} />
                        </button>

                        <button
                          className={`p-2 rounded-full transition-colors ${
                            u.ativo
                              ? "text-red-600 hover:bg-red-100 dark:text-red-300 dark:hover:bg-red-500/10"
                              : "text-green-600 hover:bg-green-100 dark:text-emerald-300 dark:hover:bg-emerald-500/10"
                          }`}
                          onClick={() => handleToggleAtivo(u)}
                          title={u.ativo ? "Desativar Usuário" : "Ativar Usuário"}
                        >
                          {u.ativo ? <ShieldOff size={18} /> : <Unlock size={18} />}
                        </button>

                        <button
                          className="p-2 text-red-600 hover:bg-red-100 rounded-full transition-colors dark:text-red-300 dark:hover:bg-red-500/10"
                          onClick={() => handleExcluir(u.id)}
                          title="Excluir Usuário"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}

                {usuarios.length === 0 && (
                  <tr>
                    <td colSpan="4" className="text-center py-8 text-gray-500 dark:text-slate-300/80">
                      Nenhum usuário encontrado.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ListaUsuarios;
